import React, { useState, useEffect } from "react";
import { Table, Tooltip } from "antd";
import { IWorkflowSearchResult } from "../../../types/IWorkflowSearchResult";
import IconTextButton from "../../../components/iconTextButton/IconTextButton";
import { ComponentsHelper } from "../../../../core/services/ComponentHelper";
import { INTRAY, REPERTOIRE, SEARCH_VIEW_WORKFLOW } from "../../../Consts";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { TableRowSelection } from "antd/lib/table/interface";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import { Open20Regular } from "@fluentui/react-icons";

interface IWorkflowSearchTableProps {
    tableContents: IWorkflowSearchResult[];
    searchResultsTableData: { fields: IRepertoireField[] };
    totalResults: number;
    selectAllWorkflow: (selectedRows: IWorkflowSearchResult[]) => void;
    selectWorkflow: (item: any, index: number) => void;
    currentPage: number;
    lookupValues: ILookupDictionary;
    resultsPerPage: number;
    updatePagination: (start: number, end: number, pageSize: number, page: number, section: string) => void;
    getUsageDetails?: (usageID: string, lookupValues?: ILookupDictionary) => void;
    getProductDetails?: (dataSource: string, productCoreID: number, isForWorkflowSession?: boolean, openNewTab?: boolean, id1?: string, id2?: string, type?: string, originOfRevision?: string, productionType?: string, musicDuration?: number[]) => void;
    getAgreementDetails?: (dataSource: string, agreementID: number) => void;
    getAdjustmentDetails?: (adjustmentId: string) => void;
    getWorkDetails?: (dataSource: string, workID: number) => void;
    setSortInfo: (sortInfo: { field: string; order: "ascend" | "descend" | undefined }) => void;
}

const WorkflowSearchTable: React.FC<IWorkflowSearchTableProps> = (props) => {
    const { tableContents, currentPage, resultsPerPage, updatePagination, selectWorkflow, selectAllWorkflow, totalResults, searchResultsTableData, setSortInfo } = props;
    const [isShiftPressed, setIsShiftPressed] = React.useState(false);
    const [pageNumber, setPageNumber] = useState(currentPage || 1);
    const [pageSize, setPageSize] = useState(resultsPerPage || 10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(() =>
        tableContents.filter((row) => row.selected).map((row) => row.ID)
    );

    useEffect(() => {
        setSelectedRowKeys(tableContents.filter((row) => row.selected).map((row) => row.ID));
    }, [tableContents]);

    useEffect(() => {
        const keyDown = (e: KeyboardEvent) => {
            if (e.key === "Shift") setIsShiftPressed(true);
        };

        const keyUp = (e: KeyboardEvent) => {
            if (e.key === "Shift") setIsShiftPressed(false);
        };

        const blur = () => setIsShiftPressed(false);

        window.addEventListener("keydown", keyDown);
        window.addEventListener("keyup", keyUp);
        window.addEventListener("blur", blur);

        return () => {
            window.removeEventListener("keydown", keyDown);
            window.removeEventListener("keyup", keyUp);
            window.removeEventListener("blur", blur);
        };
    }, []);

    const handlePageChange = (page: number, pageSize: number): void => {
        const start = (page - 1) * pageSize;
        const end = start + pageSize - 1;

        setPageNumber(page);
        setPageSize(pageSize);
        updatePagination(start, end, pageSize, page, SEARCH_VIEW_WORKFLOW);
    };

    const onClickExternalIcon = (record: IWorkflowSearchResult) => {
        const { getUsageDetails, getProductDetails, getAgreementDetails, getAdjustmentDetails, getWorkDetails, lookupValues } = props;

        const actionMap = {
            "Usage (Work Based)": () => getUsageDetails(record.entityID, lookupValues),
            "Usage (Product Based)": () => getUsageDetails(record.entityID),
            "Usage (Live Performance)": () => getUsageDetails(record.entityID),
            "Usage (Unidentified Performance)": () => getUsageDetails(record.entityID),
            "Intray Product": () => getProductDetails(record.setType, Number(record.entityID)),
            "Intray Agreement": () => getAgreementDetails(INTRAY, Number(record.entityID)),
            "Adjustment": () => getAdjustmentDetails(record.entityID),
            "Intray Work": () => getWorkDetails(INTRAY, Number(record.entityID)),
            "Repertoire Work": () => getWorkDetails(REPERTOIRE, Number(record.entityID)),
        };

        (actionMap[record.entityType])();
    };

    const getColumns = (): any[] => {
        const orderedFields = ComponentsHelper.orderFieldsBySpecificField(
            searchResultsTableData.fields.filter((field) => field.componentInstance === SEARCH_VIEW_WORKFLOW && field.name !== "noSearchResultsMessage"), "order"
        );
        return orderedFields.map((field) => {
            if (field.name === "selected" || field.name === "update_action") {
                return {
                    dataIndex: field.name,
                    title: '',
                    render: field.name === "update_action" ?
                        (_, record) => (
                            <span title={field.data}>
                                <IconTextButton
                                    icon={Open20Regular}
                                    onClick={() => onClickExternalIcon(record)}
                                />
                            </span>
                        ) : undefined,
                    sorter: false,
                    width: 0,
                };
            }
            else if (field.name === "references") {
                return {
                    dataIndex: field.name,
                    title: field.data,
                    sorter: (a, b) => handleSorting(a, b, field.name),
                    width: '25%',
                    ellipsis: { showTitle: false }, // disables the browser default title tooltip
                    render: (text: string) => (
                    <Tooltip title={text}>
                        <div
                        className="textOverFLowEllipsis"
                        >
                        {text}
                        </div>
                    </Tooltip>
                    ),
                };
            }
            else if (field.name === "values") {
                return {
                    dataIndex: field.name,
                    title: field.data,
                    sorter: false,
                };
            } else if (field.name === "entityName") {
                return {
                width: '25%',
                dataIndex: field.name,
                title: field.data,
                sorter: (a, b) => handleSorting(a, b, field.name),
                                    render: (text: string) => (
                    <Tooltip title={text}>
                        <div
                        className="textOverFLowEllipsis"
                        >
                        {text}
                        </div>
                    </Tooltip>
                    ),
            }
        } else if (field.name === "update_action") {
            return {
                dataIndex: field.name,
                title: field.data,
                sorter: (a, b) => handleSorting(a, b, field.name),
                width: 10,
            };

        } else if (field.name === "dateCreated") {
            return {
                dataIndex: field.name,
                title: field.data,
                sorter: (a, b) => handleDateSorting(a, b, field.name),
            };
        }

            return {
                dataIndex: field.name,
                title: field.data,
                sorter: (a, b) => handleSorting(a, b, field.name),
            };
        });
    };

    const handleSorting = (a, b, fieldName) => {
        
        return a[fieldName]?.localeCompare(b[fieldName])
        
    }

    const getDateSortValue = (value: string | null | undefined) => {
    if (!value) return 0;

    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return 0;

    const [, day, month, year] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return date.getTime();
    };

    const handleDateSorting = (a, b, fieldName) => {
        const dateA = getDateSortValue(a[fieldName]);
        const dateB = getDateSortValue(b[fieldName]);
        return dateA - dateB;
    }

    const rowSelection: TableRowSelection<IWorkflowSearchResult> = {
        selectedRowKeys,
        onChange: (keys,selectedRows) => {
            setSelectedRowKeys(keys)
            if (isShiftPressed) {
                selectedRows.forEach((record) => {
                    const index = tableContents.findIndex(
                    r => r.ID === record.ID
                    );

                    if (index !== -1) {
                        selectWorkflow(record, index);
                    }
                });
            }
        },
        onSelect: (record, selected) => {
            if (!isShiftPressed) 
                selectWorkflow(record, tableContents.indexOf(record));
        },
        onSelectAll: (selected, selectedRows: IWorkflowSearchResult[]) => {
            const start = (pageNumber - 1) * pageSize;
            const end = start + pageSize;
            selectAllWorkflow(selectedRows);
        },
    };
    const columns = React.useMemo(() => getColumns(), [searchResultsTableData.fields]);

    return (
        <div className="col-md-12 flexColumn">
            <Table
                columns={columns}
                dataSource={tableContents.map((item) => ({ key: item.ID, ...item }))}
                rowSelection={rowSelection}
                onChange={(pagination, filters, sorter) => {
                    if (!Array.isArray(sorter)) {
                        setSortInfo({
                            field: sorter.field as string,
                            order: sorter.order,
                        });
                    }
                }}
                pagination={{
                    current: pageNumber,
                    pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 25, 50, 100, 250, 500, 1000],
                    position: ["bottomRight", "topRight"],
                    onChange: handlePageChange,
                    showTotal: (total) => (
                        <b>Result Set: {totalResults} &nbsp;&nbsp; Return Results: {total}</b>
                    ),
                }}
                size="small"
                className="parent-table workflow-table"
            />
        </div>
    );
};

export default WorkflowSearchTable;
